import React, { useEffect } from 'react';

import { useFermetureParRetour } from '../hooks/useFermetureParRetour';
import { useFocusSurcouche } from '../hooks/useFocusSurcouche';

/**
 * Feuille montante — la seule superposition du socle (#34).
 *
 * Quatre règles, toutes issues de la décision :
 *
 * - **Superposition unique.** Deux feuilles empilées produisent deux zones de
 *   défilement concurrentes et un balayage de retour qui ne sait plus laquelle
 *   fermer. Le garde-fou ci-dessous le signale en développement plutôt que de
 *   laisser le défaut se découvrir sur un téléphone.
 * - **Contenu plafonné à ~85 %**, défilement INTERNE. #22 refusait deux
 *   défileurs imbriqués ; ici le fond est inerte, donc le défilement interne ne
 *   se dispute rien.
 * - **Pas de poignée** : elle promettrait un glissement, or le balayage
 *   horizontal appartient au système et le vertical sert à défiler.
 * - **Aucun décalage clavier.** La feuille est en `position: fixed`, et le
 *   moteur applique déjà la compensation aux éléments fixes : la composer avec
 *   `visualViewport` la doublerait (#34). C'est pourquoi `useKeyboardInset`
 *   n'apparaît pas ici — il appartient au flux, pas aux superpositions.
 *
 * Le retour système la referme (D6) — voir `useFermetureParRetour`. « Une seule
 * feuille » vaut toujours : une feuille peut vivre DANS une autre surcouche (le
 * sélecteur dans le panneau), pas au-dessus d'une autre feuille.
 */
let feuilleOuverte = false;

interface BottomSheetProps {
    ouverte: boolean;
    onClose: () => void;
    /** Nom accessible de la feuille. Requis : une boîte de dialogue anonyme n'annonce rien. */
    libelle: string;
    /**
     * Nom accessible du fond tapotable. Requis lui aussi, et pour une raison de
     * fond : le socle ne fabrique JAMAIS de texte destiné à un humain. Il n'a pas
     * de langue — `src/mobile/` n'initialise aucun i18next — et une chaîne écrite
     * ici serait un français en dur qu'aucune traduction n'atteindrait.
     */
    libelleFermeture: string;
    children: React.ReactNode;
}

export default function BottomSheet({
    ouverte,
    onClose,
    libelle,
    libelleFermeture,
    children,
}: BottomSheetProps) {
    const ref = useFocusSurcouche(ouverte);
    useFermetureParRetour(ouverte, onClose);

    useEffect(() => {
        if (!ouverte) return;

        if (feuilleOuverte && import.meta.env.DEV) {
            console.error(
                '[socle mobile] Deux feuilles montantes ouvertes en même temps : #34 n’en autorise qu’une.', // i18n-ignore : message de développement
            );
        }
        feuilleOuverte = true;

        // Échap ne ferme que la surcouche du dessus : une fenêtre ouverte par-dessus la feuille
        // (confirmation en portail du web) traite sa propre touche. Son écouteur, posé après
        // celui-ci sur window, passe APRÈS : `defaultPrevented` ne suffit donc pas, on regarde
        // aussi si la touche part d'une autre boîte de dialogue que la feuille.
        const surTouche = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            const cible = e.target instanceof Element ? e.target : null;
            const dialogue = cible?.closest('[role="dialog"]');
            if (dialogue && dialogue !== ref.current) return;
            onClose();
        };
        window.addEventListener('keydown', surTouche);

        return () => {
            feuilleOuverte = false;
            window.removeEventListener('keydown', surTouche);
        };
    }, [ouverte, onClose]);

    if (!ouverte) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end">
            {/* Le fond est inerte et ferme au tap. Il porte un libellé parce qu'il
                est tapotable, et reste hors du parcours de tabulation : la fermeture
                au clavier passe par Échap. */}
            {/* mobile-check-ignore — socle : voir Button.tsx */}
            <button
                type="button"
                aria-label={libelleFermeture}
                tabIndex={-1}
                onClick={onClose}
                className="absolute inset-0 bg-customer-space-colors/40"
            />

            <div
                ref={ref}
                role="dialog"
                aria-modal="true"
                aria-label={libelle}
                tabIndex={-1}
                className="relative max-h-[85svh] w-full overflow-y-auto rounded-t-2xl bg-white px-5 pb-8 pt-5"
            >
                {children}
            </div>
        </div>
    );
}
